import {
  createContext,
  useReducer,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { getPagePaths } from 'src/core/domain/pages';
import { getCategoryIdentifiers } from 'src/core/utils/categoryUtil';
import {
  type PageName,
  getPageName,
  getPageNumber,
  getPageSlug,
} from 'src/core/utils/pageUtil';
import { getThreadId } from 'src/core/utils/threadUtil';
import { convertToSlug } from 'src/core/utils/urlUtil';

import { useConfig } from './config';

interface PageState {
  path: string;
  pageSlug?: string;
  groupId?: string;
  categoryId: string;
  categoryName?: string;
  threadId?: string;
  pageName: PageName;
  pageNumber: number;
  isLoading: boolean;
}

type PageAction =
  | { type: 'IS_LOADING' }
  | { type: 'COMPLETE_LOADING' }
  | { type: 'SET_PAGE_NUMBER'; payload: { pageNumber: number } }
  | { type: 'SET_PAGE_NAME'; payload: { pageName: PageName } }
  | { type: 'SET_GROUP_ID'; payload: { groupId: string } };

interface PageContextType {
  pageState: PageState;
  pageDispatch: React.Dispatch<PageAction>;
}

const PageContext = createContext<PageContextType | undefined>(undefined);

const getGroupId = (
  pageName: PageName,
  params?: Record<string, string | undefined>,
): string => {
  if (pageName === 'CommunityGroupDetails' && params?.groupSlug) {
    return params?.groupSlug?.split('-')?.at(-1) || '';
  }
  return '';
};

const pageReducer = (state: PageState, action: PageAction): PageState => {
  switch (action.type) {
    case 'IS_LOADING':
      return {
        ...state,
        isLoading: true,
      };
    case 'COMPLETE_LOADING':
      return {
        ...state,
        isLoading: false,
      };
    case 'SET_PAGE_NUMBER':
      const {
        payload: { pageNumber },
      } = action;

      return {
        ...state,
        pageNumber,
      };
    case 'SET_PAGE_NAME':
      const {
        payload: { pageName },
      } = action;

      return {
        ...state,
        pageName,
        pageNumber: 1,
      };
    case 'SET_GROUP_ID':
      const {
        payload: { groupId },
      } = action;

      return {
        ...state,
        groupId,
      };
    default:
      throw new Error('pageReducer Unknown action type');
  }
};

interface PageProviderProps {
  children: ReactNode;
}

export const useUrlReloader = () => {
  const { pathname, search } = useLocation();

  // Remove ?page=1 from URL when present
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const pageOnePattern = /\?page=1($|&)/;
    if (search.match(pageOnePattern)) {
      const newSearch = search.replace(pageOnePattern, '');
      const correctUrl = `${pathname}${newSearch}`;

      window.location.href = correctUrl;
    }
  }, [search, pathname]);
};

export const useUrlGroupReloader = (categoryName: string) => {
  const { pathname, search } = useLocation();
  const params = useParams();
  const { language } = useConfig();
  const pageName = getPageName(pathname + search);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      pageName !== 'CommunityGroupDetails' ||
      !categoryName
    ) {
      return;
    }

    const pageSlug = getPageSlug(pageName, params);
    const originalSlug = convertToSlug(categoryName);
    const groupId = getGroupId(pageName, params);

    if (pageSlug !== originalSlug) {
      const communitiesPath = getPagePaths({ language }).CommunityGroups;
      const correctUrl = `${communitiesPath}/${originalSlug}-${groupId}`;
      window.location.href = correctUrl;
    }
  }, [categoryName, language, params, pageName]);
};

export const useUrlThreadReloader = (slug: string, groupSlug: string) => {
  const { pathname, search } = useLocation();
  const params = useParams();
  const { language } = useConfig();
  const pageName = getPageName(pathname + search);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      pageName !== 'CommunityThreadDetails' ||
      !slug
    ) {
      return;
    }

    const pageSlug = getPageSlug(pageName, params);
    const slugWithoutId = slug.split('-')?.slice(0, -1).join('-');

    if (pageSlug !== slugWithoutId || params?.groupSlug !== groupSlug) {
      const correctUrl = getPagePaths({
        language,
        pathParams: { threadSlug: slug, groupSlug },
      }).CommunityThreadDetail;
      window.location.href = correctUrl;
    }
  }, [slug, language, params, pageName, groupSlug]);
};

export const PageProvider: React.FC<PageProviderProps> = ({ children }) => {
  const { pathname, search } = useLocation();
  const params = useParams();
  const pageName = getPageName(pathname + search);
  const category = getCategoryIdentifiers(pageName, params);
  const [pageState, pageDispatch] = useReducer(pageReducer, {
    path: pathname,
    pageName,
    isLoading: false,
    pageNumber: getPageNumber(search),
    threadId: getThreadId(pageName, params),
    groupId: getGroupId(pageName, params),
    pageSlug: getPageSlug(pageName, params),
    ...category,
  });

  useUrlReloader();
  return (
    <PageContext.Provider
      value={{
        pageState,
        pageDispatch,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePage = (): PageContextType => {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error('usePage must be used within a PageProvider');
  }
  return context;
};

export default PageProvider;
